import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React from 'react'

import heroBg from './assets/hero-bg.png'
import logo from './assets/logo.svg'

const HERO_HEADING_LINE_1 = 'The future of business'
const HERO_HEADING_LINE_2 = 'will run on intelligence,'
const HERO_HEADING = `${HERO_HEADING_LINE_1} ${HERO_HEADING_LINE_2}`
const HERO_HEADING_TAIL = 'not software.'
const LOGO_ALT = 'keyrus Ai — Architect of intelligence'

const getStyles = (theme, isMobile) => {
  const deepNavy = theme.palette?.background?.deepNavy || '#171531'
  const white = theme.palette?.text?.light || '#ffffff'
  const cyan = theme.palette?.primary?.main || '#28c9ff'
  const blue = theme.palette?.primary?.light || '#06bdff'
  const red = theme.palette?.tertiary?.main || '#df0a21'
  const orange = theme.palette?.secondary?.main || '#f39620'
  const accentGradient = `linear-gradient(90deg, ${cyan} 0%, ${blue} 33%, ${red} 63%, ${orange} 100%)`
  const navSpacerHeight = isMobile ? 56 : 0

  return {
    root: {
      position: 'relative',
      minHeight: isMobile ? 404 : '90vh',
      width: '100%',
      background: `radial-gradient(150% 115% at 50% 0%, rgba(23, 21, 49, 0) 0%, rgba(23, 21, 49, 0) 34%, ${deepNavy} 100%), url(${heroBg}) center top/cover no-repeat, ${deepNavy}`,
      color: white,
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: isMobile ? 'stretch' : 'center',
    },
    accentTop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: accentGradient,
      zIndex: 2,
    },
    inner: {
      position: 'relative',
      width: '100%',
      maxWidth: 1440,
      margin: '0 auto',
      padding: isMobile ? '20px 16px 36px' : '36px 24px 120px',
      display: 'grid',
      gap: isMobile ? 18 : 56,
      justifyItems: 'center',
      textAlign: 'center',
      zIndex: 1,
    },
    navSpacer: {
      width: 1,
      height: navSpacerHeight,
      pointerEvents: 'none',
    },
    logoSlot: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      marginTop: isMobile ? 6 : 0,
    },
    logo: {
      width: isMobile ? 116 : 'min(290px, 15.5vw)',
      height: 'auto',
    },
    heading: {
      margin: '8px 0 0',
      maxWidth: '100%',
      fontSize: 'clamp(32px, 5.5vw, 100px)',
      lineHeight: isMobile ? 1.12 : 1.07,
      fontWeight: 800,
      color: white,
      textAlign: 'center',
    },
    headingTail: {
      display: 'block',
      marginTop: 8,
      fontSize: 'clamp(24px, 3.5vw, 64px)',
      fontWeight: 400,
      lineHeight: 1.1,
      color: white,
    },
  }
}

const KeyrusAiHero = ({copy, isMobile}) => {
  const theme = useTheme()
  const styles = getStyles(theme, isMobile)
  const line1 = copy.line1 || HERO_HEADING_LINE_1
  const line2 = copy.line2 || HERO_HEADING_LINE_2
  const tail = copy.tail || HERO_HEADING_TAIL
  const headingInline = copy.line1 ? `${line1} ${line2}` : HERO_HEADING

  return (
    <section id="hero" style={styles.root}>
      <span aria-hidden="true" style={styles.accentTop} />
      <div style={styles.inner}>
        <span aria-hidden="true" style={styles.navSpacer} />
        <div style={styles.logoSlot}>
          <img alt={LOGO_ALT} src={logo} style={styles.logo} />
        </div>
        <h1 style={styles.heading}>
          {isMobile ? (
            headingInline
          ) : (
            <>
              {line1}
              <br />
              {line2}
            </>
          )}
          <span style={styles.headingTail}>{tail}</span>
        </h1>
      </div>
    </section>
  )
}

KeyrusAiHero.propTypes = {
  copy: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    tail: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
}

KeyrusAiHero.defaultProps = {
  copy: {},
  isMobile: false,
}

export default KeyrusAiHero
